import React from "react"
import PropTypes from "prop-types"
import { styled } from "@mui/material/styles"
import { css } from "@emotion/react"
import {
  Button,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
} from "@mui/material"
import { StyledEngineProvider } from "@mui/material/styles"
import axios from "axios"
import { logEvent } from "../utils/analytics"

const CancellationFlowContainer = styled("div")`
  display: flex;
  justify-content: center;
  margin: 0 10px;
  width: 100%;
`

const CancellationFlowCard = styled("div")`
  width: 500px;
  margin: 50px 0;

  label {
    color: inherit;
  }
`

const PauseText = styled("p")`
  font-size: 17px;
  text-align: justify;
`

const SFTextField = styled(TextField)`
  .MuiInputBase-root {
    font-family: Lato;
  }
`

const ButtonsContainer = styled("div")`
  display: flex;
  flex-direction: column;
`

const SFButton = styled(Button)(({ noMarginRight }) => ({
  marginTop: "20px",
  marginRight: noMarginRight ? "0" : "20px",
}))

const OPTIONS_REQUIRING_MORE_INFO = [
  "dont-like-sounds",
  "using-other-service",
  "other-reason",
  "workflow-fit",
]

class CancellationFlow extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      cancellationReason: undefined,
      viewToShow: "reasonView",
      isContinueEnabled: false,
    }
  }

  componentDidMount() {
    logEvent("Visited cancellation page")
  }

  handleRadioSelect = (e) => {
    const radioValue = e.target.value
    let isContinueEnabled = !OPTIONS_REQUIRING_MORE_INFO.includes(radioValue)
    this.setState({
      cancellationReason: radioValue,
      additionalInfo: null,
      isContinueEnabled: isContinueEnabled,
    })
  }

  handleTextField = (e) => {
    const text = e.target.value
    const isContinueEnabled = text.length > 0
    this.setState({
      additionalInfo: text,
      isContinueEnabled: isContinueEnabled,
    })
  }

  handleContinue = () => {
    const { shouldShowDiscountScreen } = this.props
    if (shouldShowDiscountScreen) {
      this.setState({ viewToShow: "discountView" })
    } else {
      this.setState({ viewToShow: "confirmationView" })
    }
  }

  handleNoThanks = () => {
    this.setState({ viewToShow: "confirmationView" })
  }

  handleCancel = () => {
    const { userSlug } = this.props
    const baseURL = window.location.origin
    window.location.href = baseURL + `/users/${userSlug}/subscriptions`
  }

  handleAcceptDiscount = async () => {
    const { acceptDiscountURL, csrfToken } = this.props
    const { cancellationReason, additionalInfo } = this.state
    const feedbackText = `${cancellationReason} - ${additionalInfo}`

    axios.defaults.headers.common["X-CSRF-Token"] = csrfToken

    try {
      const response = await axios({
        method: "post",
        responseType: "json",
        url: acceptDiscountURL,
        timeout: 15000,
        data: {
          url: window.location.href,
          feedback_text: feedbackText,
          authenticity_token: csrfToken,
        },
      })
      this.setState({ viewToShow: "discountSuccessView" })
      logEvent("Accepted discount")
    } catch (error) {
      console.error("save error", error)
      this.setState({ viewToShow: "errorView" })
    }
  }

  handleAcceptPause = async () => {
    const { pauseURL, csrfToken } = this.props
    axios.defaults.headers.common["X-CSRF-Token"] = csrfToken

    try {
      const response = await axios({
        method: "post",
        responseType: "json",
        url: pauseURL,
        timeout: 15000,
        data: {
          url: window.location.href,
          authenticity_token: csrfToken,
        },
      })
      this.setState({ viewToShow: "pausedView" })
      logEvent("Pause subscription")
    } catch (error) {
      console.error("Pause error", error)
      this.setState({ viewToShow: "errorView" })
    }
  }

  handleSubmit = async () => {
    const { deleteURL, csrfToken } = this.props
    const { cancellationReason, additionalInfo } = this.state
    const feedbackText = `${cancellationReason} - ${additionalInfo}`

    axios.defaults.headers.common["X-CSRF-Token"] = csrfToken

    try {
      const response = await axios({
        method: "delete",
        responseType: "json",
        url: deleteURL,
        timeout: 15000,
        data: {
          url: window.location.href,
          feedback_text: feedbackText,
          authenticity_token: csrfToken,
        },
      })
      this.setState({ viewToShow: "successView" })
      logEvent("Subscription cancelled")
    } catch (error) {
      console.error("save error", error)
      this.setState({ viewToShow: "errorView" })
    }
  }

  handleGoToHomepage = () => {
    const baseURL = window.location.origin
    window.location.href = baseURL
  }

  renderCancellationReason() {
    const { greetingName } = this.props
    const { cancellationReason, isContinueEnabled } = this.state

    return (
      <>
        <p>Sorry to see you go {greetingName}.</p>

        <p>
          You’ll always be able to re-download any of the samples you’ve already
          downloaded.
        </p>

        <p>
          We’re a tiny team and it would be incredibly helpful to understand why
          you’re canceling. Would you mind telling us why you’re no longer
          interested in a subscription?
        </p>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="gender"
            name="gender1"
            value={cancellationReason}
            onChange={this.handleRadioSelect}
          >
            <FormControlLabel
              value="not-producing"
              control={<Radio color="primary" />}
              label="I'm not producing any content that need samples right now."
            />
            <FormControlLabel
              value="dont-like-sounds"
              control={<Radio color="primary" />}
              label="I can't find sounds I like."
            />
            {cancellationReason === "dont-like-sounds" && (
              <SFTextField
                multiline
                rows={4}
                placeholder="Sounds I'm looking for"
                variant="outlined"
                onChange={this.handleTextField}
              />
            )}
            <FormControlLabel
              value="workflow-fit"
              control={<Radio color="primary" />}
              label="It doesn't fit my workflow."
            />
            {cancellationReason === "workflow-fit" && (
              <SFTextField
                multiline
                rows={1}
                placeholder="Why not?"
                variant="outlined"
                onChange={this.handleTextField}
              />
            )}
            <FormControlLabel
              value="cant-afford"
              control={<Radio color="primary" />}
              label="I can't afford it right now."
            />
            <FormControlLabel
              value="enough-credits"
              control={<Radio color="primary" />}
              label="I have enough credits."
            />
            <FormControlLabel
              value="using-other-service"
              control={<Radio color="primary" />}
              label="I am using another service."
            />
            {cancellationReason === "using-other-service" && (
              <SFTextField
                multiline
                rows={1}
                placeholder="Name of service"
                variant="outlined"
                onChange={this.handleTextField}
              />
            )}
            <FormControlLabel
              value="other-reason"
              control={<Radio color="primary" />}
              label="Other reason:"
            />
            {cancellationReason === "other-reason" && (
              <SFTextField
                multiline
                rows={4}
                placeholder="Why are you canceling?"
                variant="outlined"
                onChange={this.handleTextField}
              />
            )}
          </RadioGroup>
        </FormControl>
        <SFButton
          variant="contained"
          color="primary"
          size="large"
          onClick={this.handleContinue}
          disabled={!isContinueEnabled}
          key="continueBtn"
        >
          Continue
        </SFButton>
        <SFButton
          color="primary"
          size="large"
          onClick={this.handleCancel}
          key="nevermindBtn1"
        >
          Nevermind
        </SFButton>
      </>
    )
  }

  renderDiscount() {
    const { greetingName, isMonthly } = this.props
    return (
      <>
        <p className="flow-text bold">
          Thank you for your feedback {greetingName}!
        </p>
        <p className="flow-text">
          You're one of our favorites and we don't want to see you go...
        </p>
        <p className="flow-text">
          If you stick around, you'll get 30% off every{" "}
          {isMonthly ? "month" : "year"} from now on!
        </p>
        <SFButton
          variant="contained"
          size="large"
          color="primary"
          onClick={this.handleAcceptDiscount}
          key="illTakeItBtn"
        >
          I'll take it!
        </SFButton>
        <SFButton
          color="secondary"
          size="large"
          onClick={this.handleNoThanks}
          key="noThanksBtn"
        >
          I'd like to continue to cancel
        </SFButton>
      </>
    )
  }

  renderConfirmation() {
    const {
      subscriptionPeriodEnd,
      subscriptionCreditsCount,
      remainingCreditsCount,
      isPausable,
      billingResumesAt,
    } = this.props
    const creditsCountAfterCancellation =
      remainingCreditsCount - subscriptionCreditsCount

    return (
      <>
        <PauseText>
          If you'd still like to cancel, your subscription will end at the end
          of your billing cycle on <em>{subscriptionPeriodEnd}</em>.
        </PauseText>

        <PauseText className="bold">
          {" "}
          On that day all your subscription credits will be put on hold.
        </PauseText>
        <PauseText>
          If you decide to re-subscribe, the {subscriptionCreditsCount} credits
          that you accumulated as part of your plan will be available to you
          again.
          {creditsCountAfterCancellation > 0 && (
            <>
              {" "}
              The remaining {creditsCountAfterCancellation} credits earned from
              uploading or referrals will still remain in your account.
            </>
          )}
        </PauseText>

        <Divider />

        {isPausable && (
          <div>
            <PauseText>
              As an alternative, your subscription can be paused for up to 2
              months from the end of your next billing cycle. Pausing your
              subscription will{" "}
              <strong>
                prevent your {subscriptionCreditsCount} from being out on hold
              </strong>
              .
            </PauseText>

            <PauseText>
              Upon pausing, your subscription will be resumed on{" "}
              <strong>{billingResumesAt}</strong>.
            </PauseText>
          </div>
        )}

        <ButtonsContainer>
          {isPausable && (
            <SFButton
              color="primary"
              size="large"
              onClick={this.handleAcceptPause}
              key="pauseBtn"
              noMarginRight
            >
              Pause my subscription
            </SFButton>
          )}
          <SFButton
            color="secondary"
            size="large"
            onClick={this.handleSubmit}
            key="cancelBtn"
            noMarginRight
          >
            Cancel my subscription
          </SFButton>
          <SFButton
            size="large"
            color="primary"
            variant="outlined"
            onClick={this.handleCancel}
            key="nevermindBtn2"
            noMarginRight
          >
            Nevermind
          </SFButton>
        </ButtonsContainer>
      </>
    )
  }

  renderPauseSuccess() {
    const { billingResumesAt } = this.props

    return (
      <div>
        <p className="flow-text bold">
          Your subscription is paused and will renew on {billingResumesAt}.
        </p>
        <SFButton
          variant="contained"
          size="large"
          color="primary"
          onClick={this.handleGoToHomepage}
          key="goToHomepageBtn"
        >
          Go back to homepage
        </SFButton>
      </div>
    )
  }

  renderDiscountSuccess() {
    return (
      <div>
        <p
          css={css`
            font-size: 40px;
            font-weight: 600;
            line-height: 64px;
          `}
        >
          Woohoo 🙌 <br /> Your subscription is now 30% off!
        </p>
        <video height="330" autoPlay loop muted>
          <source
            src="https://s3-us-west-1.amazonaws.com/assets.samplefocus.com/celebration.mp4"
            type="video/mp4"
          />
        </video>
        <SFButton
          variant="contained"
          size="large"
          color="primary"
          onClick={this.handleGoToHomepage}
          key="goToHomepageBtn"
        >
          Go back to homepage
        </SFButton>
      </div>
    )
  }

  renderSuccess() {
    return (
      <>
        <p className="flow-text bold">
          Your subscription has succesfully been cancelled.
        </p>
        <SFButton
          variant="contained"
          size="large"
          color="primary"
          onClick={this.handleGoToHomepage}
          key="goToHomepageBtn2"
        >
          Go back to homepage
        </SFButton>
      </>
    )
  }

  renderError() {
    return (
      <>
        <p className="flow-text bold">
          Something went wrong. Please try again.
        </p>
      </>
    )
  }

  renderSubView() {
    const { viewToShow } = this.state
    if (viewToShow === "reasonView") {
      return this.renderCancellationReason()
    } else if (viewToShow === "confirmationView") {
      return this.renderConfirmation()
    } else if (viewToShow === "discountView") {
      return this.renderDiscount()
    } else if (viewToShow === "successView") {
      return this.renderSuccess()
    } else if (viewToShow === "discountSuccessView") {
      return this.renderDiscountSuccess()
    } else if (viewToShow === "errorView") {
      return this.renderError()
    } else if (viewToShow === "pausedView") {
      return this.renderPauseSuccess()
    }
  }

  render() {
    return (
      <StyledEngineProvider injectFirst>
        <CancellationFlowContainer>
          <CancellationFlowCard>{this.renderSubView()}</CancellationFlowCard>
        </CancellationFlowContainer>
      </StyledEngineProvider>
    )
  }
}

CancellationFlow.propTypes = {
  greetingName: PropTypes.string,
  userSlug: PropTypes.string,
  subscriptionPeriodEnd: PropTypes.string,
  subscriptionCreditsCount: PropTypes.number,
}

export default CancellationFlow
